import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import {
    faAddressBook,
    faAlignLeft,
    faArchive,
    faBalanceScale,
    faBan,
    faBank,
    faBars,
    faBook,
    faBookmark,
    faBorderAll,
    faBoxArchive,
    faBoxesStacked,
    faBrain,
    faCashRegister,
    faChalkboardTeacher,
    faChartArea,
    faChartPie,
    faCircleInfo,
    faClock,
    faClockRotateLeft,
    faCog,
    faComment,
    faDesktop,
    faDollarSign,
    faEnvelope,
    faFileImport,
    faFileInvoice,
    faHistory,
    faHorse,
    faImages,
    faInbox,
    faKey,
    faLayerGroup,
    faLineChart,
    faLink,
    faList,
    faListCheck,
    faLock,
    faMagicWandSparkles,
    faMailBulk,
    faMobile,
    faPlug,
    faNewspaper,
    faPalette,
    faPuzzlePiece,
    faRotateLeft,
    faRuler,
    faServer,
    faSign,
    faSolarPanel,
    faStethoscope,
    faStoreAlt,
    faSync,
    faSyncAlt,
    faTable,
    faTabletScreenButton,
    faTicketAlt,
    faTrophy,
    faUser,
    faUserCheck,
    faUserClock,
    faUsers,
    faUserTag,
    faUtensils,
    faWarehouse,
    faWheatAwn,
    faWindowMaximize,
    faWindowRestore
} from '@fortawesome/free-solid-svg-icons';
import { buildSettingsModules } from '@ui/SettingsPanel/settingsConfig';
import i18n from 'i18n';
import React from 'react';

import type { ClubModuleKey } from './clubModules.generated';
import type { Tab } from './ModulePageLayout';

export type Page = {
    title: string;
    link: string;
    /** Canonical module key, mirrored from laravel/app/Enums/ClubModule.php.
     *
     *  Required on purpose: it is what `club_settings.modules.visible` stores, so the
     *  compiler — not a review checklist — enforces that every new module registers one.
     *
     *  Deliberately separate from the last URL segment even though the two are identical
     *  today: the segment is derived from routing, and renaming a route would silently
     *  change the key every club has stored. */
    moduleKey: ClubModuleKey;
    /** Accordion grouping only — no standalone route. Skips the page-level Cmd+K entry
     *  (the tabs still link to real routes). Used by the Einstellungen hub. */
    virtual?: boolean;
    icon?: IconDefinition;
    beta?: boolean;
    experimental?: string;
    /** Plan key required to access this module, e.g. 'trainingplantool'. */
    requiredPlan?: string;
    infoBadge?: {
        icon: IconDefinition;
        tooltip: string;
    };
    tabs?: Tab[];
    /** Optional quick action shown next to the module title.
     *  Fires without navigating — ideal for "Neu erstellen" flows. */
    quickAction?: {
        label: string;
        icon?: IconDefinition;
        prominent?: boolean;
        onClick: (e: React.MouseEvent) => void;
    };
    quickActions?: Array<{
        label: string;
        icon?: IconDefinition;
        prominent?: boolean;
        onClick: (e: React.MouseEvent) => void;
    }>;
};

interface BuildPagesParams {
    clubSlug: string;
    updateCount?: any;
    /** Open space manager booking requests. SideBarAccordion only renders numeric badges. */
    allocationRequestCount?: number;
    /** Unread event bookings. SideBarAccordion only renders numeric badges. */
    unreadBookingCount?: number;
    /** Unread mails in the club inbox. */
    unreadInboxCount?: number;
    invoiceTabs?: Tab[];
    /** False hides the banking tabs that stay empty until a bank is connected.
     *  Defaults to true so callers that don't know the connection state show everything. */
    hasBankConnection?: boolean;
}

export const buildClubPages = ({
    clubSlug,
    updateCount,
    allocationRequestCount,
    unreadBookingCount,
    unreadInboxCount,
    invoiceTabs,
    hasBankConnection = true
}: BuildPagesParams): Page[] => {
    const routePrefix = `/club/${clubSlug}`;

    return [
        {
            // Translated module (see docs/i18n.md) — titles resolve via i18n so the
            // sidebar follows the active locale. Caller must rebuild on language change.
            title: i18n.t('general:nav.title'),
            link: `${routePrefix}/general`,
            moduleKey: 'general',
            icon: faStoreAlt,
            tabs: [
                {
                    key: '',
                    title: i18n.t('general:nav.settings'),
                    icon: faCog,
                    permissions: ['club.settings']
                },

                {
                    key: 'permissions',
                    title: i18n.t('general:nav.permissions'),
                    icon: faBalanceScale,
                    permissions: [],
                    group: 'settings',
                    tooltip: i18n.t('general:nav.permissionsTooltip')
                },
                {
                    key: 'modules',
                    title: i18n.t('general:nav.modules'),
                    icon: faPuzzlePiece,
                    permissions: ['club.settings'],
                    group: 'settings'
                },
                {
                    key: 'app',
                    title: i18n.t('general:nav.app'),
                    icon: faMobile,
                    permissions: ['club.settings'],
                    group: 'settings'
                },
                {
                    key: 'chat',
                    title: i18n.t('general:nav.chat'),
                    icon: faComment,
                    permissions: ['club.settings'],
                    group: 'settings'
                },
                {
                    key: 'mollie',
                    title: i18n.t('general:nav.mollie'),
                    icon: faCashRegister,
                    permissions: ['club.mollie'],
                    group: 'settings'
                }
            ]
        },
        {
            title: i18n.t('cms:nav.title'),
            link: `${routePrefix}/cms`,
            moduleKey: 'cms',
            icon: faSolarPanel,
            beta: true,
            tabs: [
                {
                    key: '',
                    title: i18n.t('cms:nav.pages'),
                    icon: faList,
                    permissions: ['club.settings'],
                    // Editing a page (/cms/editor/…) or managing one (/cms/pages/…)
                    // is still being in the pages section, so neither must leave
                    // the module unmarked.
                    activeKeys: ['editor', 'pages']
                },
                {
                    // Replaced "Seite mit KI", which was a second door to something the
                    // page list's own "Neue Seite" dialog already offers. The menu had no
                    // door at all: it was a number typed into one page's settings.
                    key: 'menu',
                    title: i18n.t('cms:nav.menu'),
                    icon: faBars,
                    permissions: ['club.settings']
                },
                {
                    // The pictures the website uses, and where. Before, a club's images
                    // could only be reached from inside a block, one at a time.
                    key: 'images',
                    title: i18n.t('cms:nav.images'),
                    icon: faImages,
                    permissions: ['club.settings']
                },
                {
                    key: 'design',
                    title: i18n.t('cms:nav.design'),
                    icon: faPalette,
                    // Its own right, because this screen is the only one that changes
                    // every page of the club at once — and does it the moment it saves.
                    // A club owner has every right by default, so nobody who could reach
                    // this yesterday loses it today; what changes is that "may fix a
                    // typo" can now be granted without "may repaint the website".
                    permissions: ['club.settings', 'cms.design'],
                    group: 'settings'
                }
            ]
        },
        {
            title: i18n.t('ai:nav.title'),
            link: `${routePrefix}/ai`,
            moduleKey: 'ai',
            icon: faMagicWandSparkles,
            beta: true,
            tabs: [
                {
                    key: '',
                    title: i18n.t('ai:nav.knowledgeBase'),
                    icon: faBrain,
                    permissions: ['club.settings']
                },
                {
                    key: 'ai-approvals',
                    title: i18n.t('ai:nav.approvals'),
                    icon: faUserCheck,
                    permissions: ['club.settings']
                },
                {
                    key: 'ai-history',
                    title: i18n.t('ai:nav.history'),
                    icon: faHistory,
                    permissions: [''],
                    group: 'settings'
                }
            ]
        },
        {
            title: i18n.t('message-management:nav.title'),
            link: `${routePrefix}/messages`,
            moduleKey: 'messages',
            icon: faEnvelope,
            tabs: [
                {
                    key: 'inbox',
                    title: i18n.t('message-management:nav.inbox'),
                    icon: faInbox,
                    permissions: ['message.inbox.view'],
                    badge: unreadInboxCount
                },
                {
                    key: '',
                    title: i18n.t('message-management:nav.sent'),
                    icon: faMailBulk,
                    permissions: ['message.view']
                },
                {
                    key: 'templates',
                    title: i18n.t('message-management:nav.templates'),
                    icon: faAlignLeft,
                    permissions: ['message.template.view'],
                    group: 'settings'
                },
                {
                    key: 'email',
                    title: i18n.t('message-management:nav.email'),
                    icon: faEnvelope,
                    permissions: ['club.settings'],
                    group: 'settings'
                }
            ]
        },
        {
            // Translated module (see docs/i18n.md) — titles resolve via i18n so the
            // sidebar follows the active locale. Caller must rebuild on language change.
            title: i18n.t('feed:nav.title'),
            link: `${routePrefix}/feed`,
            moduleKey: 'feed',
            icon: faNewspaper,
            tabs: [
                {
                    key: '',
                    title: i18n.t('feed:nav.subscribers'),
                    icon: faMailBulk,
                    permissions: ['message.view']
                },
                {
                    key: 'posts',
                    title: i18n.t('feed:nav.posts'),
                    icon: faNewspaper,
                    permissions: ['feed.post.create']
                },
                {
                    key: 'content-groups',
                    title: i18n.t('feed:nav.contentGroups'),
                    icon: faAlignLeft,
                    permissions: ['message.template.view'],
                    group: 'settings'
                }
            ]
        },
        {
            title: i18n.t('member-management:nav.title'),
            link: `${routePrefix}/members`,
            moduleKey: 'members',
            icon: faUsers,
            requiredPlan: 'clubmembermanagementtool',
            tabs: [
                {
                    key: '',
                    title: i18n.t('member-management:nav.dashboard'),
                    icon: faSolarPanel,
                    permissions: ['member_management.view']
                },
                {
                    key: 'members',
                    title: i18n.t('member-management:nav.members'),
                    icon: faUser,
                    permissions: ['member_management.view']
                },
                {
                    key: 'categories',
                    title: i18n.t('member-management:nav.categories'),
                    icon: faUserTag,
                    permissions: ['member_management.category.view'],
                    group: 'settings'
                },
                {
                    key: 'departments',
                    title: i18n.t('member-management:nav.departments'),
                    icon: faLayerGroup,
                    permissions: ['member_management.category.view'],
                    group: 'settings'
                },
                {
                    key: 'groups',
                    title: i18n.t('member-management:nav.groups'),
                    icon: faUsers,
                    permissions: ['member_management.category.view'],
                    group: 'settings',
                    tooltip: i18n.t('member-management:nav.groupsTooltip')
                },
                {
                    key: 'lockers',
                    title: i18n.t('member-management:nav.lockers'),
                    icon: faKey,
                    permissions: ['member_management.locker.view']
                },
                {
                    key: 'locker-assignments',
                    title: i18n.t('member-management:nav.lockerAssignments'),
                    icon: faClockRotateLeft,
                    permissions: ['member_management.locker.view'],
                    group: 'settings'
                },
                {
                    key: 'statistics',
                    title: i18n.t('member-management:nav.statistics'),
                    icon: faChartArea,
                    permissions: ['member_management.view']
                },
                {
                    key: 'applications',
                    title: i18n.t('member-management:nav.applications'),
                    icon: faFileInvoice,
                    permissions: ['member_management.application.view']
                },
                {
                    key: 'cancellations',
                    title: i18n.t('member-management:nav.cancellations'),
                    icon: faBan,
                    permissions: ['member_management.application.view'],
                    badge: updateCount
                }
            ]
        },
        {
            // Experimental equestrian module. Only accounts carrying the `equestrian_v1`
            // flag in user.experimental_features ever see it in the navigation.
            //
            // Horse record and horse care share ONE module on purpose. They are worthless
            // apart: a care entry always hangs off a horse, and a horse without its care
            // history is a name on a list. Two navigation entries would also have meant two
            // module keys, and a club could have hidden one half of a whole.
            title: i18n.t('equestrian:nav.title'),
            link: `${routePrefix}/horses`,
            moduleKey: 'horses',
            icon: faHorse,
            experimental: 'equestrian_v1',
            beta: true,
            tabs: [
                {
                    key: '',
                    title: i18n.t('equestrian:nav.horses'),
                    icon: faList,
                    permissions: ['equestrian.horse.view']
                },
                {
                    // No `permissions`: the two entry rights (health / service) are an OR
                    // here, while the layout check requires ALL listed permissions. The
                    // list resolves them per category and explains a missing right itself.
                    key: 'due',
                    title: i18n.t('horseCare:nav.due'),
                    icon: faClock
                },
                {
                    key: 'care',
                    title: i18n.t('horseCare:nav.entries'),
                    icon: faStethoscope
                },
                {
                    key: 'care-types',
                    title: i18n.t('horseCare:nav.types'),
                    icon: faBook,
                    permissions: ['equestrian.care_type.view'],
                    group: 'settings'
                },
                {
                    key: 'contacts',
                    title: i18n.t('horseCare:nav.contacts'),
                    icon: faAddressBook,
                    permissions: ['equestrian.contact.view'],
                    group: 'settings'
                },
                {
                    key: 'occupancy',
                    title: i18n.t('equestrian:nav.occupancy'),
                    icon: faWarehouse,
                    permissions: ['equestrian.stable.view']
                },
                {
                    key: 'assignments',
                    title: i18n.t('equestrian:nav.assignments'),
                    icon: faHistory,
                    permissions: ['equestrian.stable.view']
                },
                {
                    key: 'feeding-list',
                    title: i18n.t('equestrian:nav.feedingList'),
                    icon: faUtensils,
                    permissions: ['equestrian.feeding_plan.view']
                },
                {
                    key: 'feeding',
                    title: i18n.t('equestrian:nav.feeding'),
                    icon: faWheatAwn,
                    permissions: ['equestrian.feeding_plan.view']
                },
                {
                    key: 'stables',
                    title: i18n.t('equestrian:nav.stables'),
                    icon: faWarehouse,
                    permissions: ['equestrian.stable.view'],
                    group: 'settings'
                },
                {
                    key: 'feeding-settings',
                    title: i18n.t('equestrian:nav.feedingSettings'),
                    icon: faWheatAwn,
                    permissions: ['equestrian.feeding_catalogue.view'],
                    group: 'settings'
                },
                {
                    // Its own right, not care_service.*: recording a billable service is
                    // the stable's daily work, invoicing a month of it is the office's.
                    key: 'billing',
                    title: i18n.t('horseCare:nav.billing'),
                    icon: faFileInvoice,
                    permissions: ['equestrian.billing.run'],
                    group: 'settings'
                }
            ]
        },
        {
            title: i18n.t('space-manager:nav.title'),
            link: `/club/${clubSlug}/space-manager`,
            moduleKey: 'space-manager',
            icon: faBorderAll,
            requiredPlan: 'spacebookingtool',
            tabs: [
                {
                    key: '',
                    title: i18n.t('space-manager:nav.dashboard'),
                    icon: faSign,
                    permissions: ['space_manager.statistics.view']
                },
                {
                    key: 'allocations',
                    title: i18n.t('space-manager:nav.allocations'),
                    icon: faClock,
                    permissions: ['space_manager.allocations.view']
                },
                {
                    key: 'requests',
                    title: i18n.t('space-manager:nav.requests'),
                    icon: faInbox,
                    permissions: ['space_manager.allocations.view', 'space_manager.allocations.approve'],
                    badge: allocationRequestCount
                },
                {
                    key: 'booking-links',
                    title: i18n.t('space-manager:nav.bookingLinks'),
                    icon: faLink,
                    permissions: ['space_manager.allocations.view']
                },
                {
                    key: 'rules',
                    title: i18n.t('space-manager:nav.rules'),
                    icon: faRuler,
                    permissions: ['space_manager.rules.view'],
                    group: 'settings'
                },
                {
                    key: 'trainer',
                    title: i18n.t('space-manager:nav.trainer'),
                    icon: faChalkboardTeacher,
                    permissions: ['space_manager.rules.view'],
                    group: 'settings'
                },
                {
                    key: 'addons',
                    title: i18n.t('space-manager:nav.addons'),
                    icon: faPuzzlePiece,
                    permissions: ['space_manager.addons.view'],
                    group: 'settings'
                },
                {
                    key: 'subscription-templates',
                    title: i18n.t('space-manager:nav.subscriptionTemplates'),
                    icon: faSync,
                    permissions: ['space_manager.subscription_templates.view'],
                    group: 'settings'
                },
                {
                    key: 'control',
                    title: i18n.t('space-manager:nav.control'),
                    icon: faServer,
                    group: 'settings',
                    tooltip: i18n.t('space-manager:nav.controlTooltip')
                }
            ]
        },
        {
            title: 'Bank',
            link: `${routePrefix}/banking`,
            moduleKey: 'banking',
            icon: faBank,
            // Vorerst am Rechnungs-Abo mitverkauft: derselbe Plan-Key wie /invoices, ein
            // gebuchtes 'invoicetool' schaltet Banking mit frei. Muss zum Middleware-Gate der
            // Banking-Routen (routes/api.php) passen — sonst zeigt die Seite den Upgrade-Hinweis
            // nicht, sondern läuft in 403er der einzelnen Requests.
            requiredPlan: 'invoicetool',
            // Konten und Umsätze haben ohne Bankverbindung keinen Inhalt — bis dahin bleibt nur
            // die Übersicht mit dem Setup. Die Einstellungen liegen im globalen Einstellungen-Hub
            // (siehe settingsConfig), nicht als eigener Tab hier. Der frühere Tab "Bankabgleich"
            // ist in "Umsätze" aufgegangen.
            tabs: [
                {
                    key: '',
                    title: 'Übersicht',
                    icon: faLineChart,
                    permissions: ['banking.view']
                },
                // Umsätze vor Konten: das ist die Seite, auf der täglich gearbeitet wird, die
                // Kontoliste schaut man einmal beim Einrichten an.
                ...(hasBankConnection
                    ? [
                          {
                              key: 'transactions',
                              title: 'Umsätze',
                              icon: faList,
                              permissions: ['banking.view']
                          },
                          {
                              key: 'accounts',
                              title: 'Konten',
                              icon: faBank,
                              permissions: ['banking.view']
                          }
                      ]
                    : [])
            ]
        },
        {
            title: 'Headless API',
            link: `${routePrefix}/headless-api`,
            moduleKey: 'headless-api',
            icon: faServer,
            experimental: 'headless_api',
            beta: true,
            tabs: [
                {
                    key: '',
                    title: 'API Tokens',
                    icon: faKey,
                    permissions: ['headless_api.manage']
                },
                {
                    key: 'logs',
                    title: 'Logs',
                    link: `${routePrefix}/headless-api/logs`,
                    icon: faList,
                    permissions: ['headless_api.logs', 'headless_api.manage'],
                    tooltip: 'Die Daten werden nach 14 Tagen automatisch gelöscht.'
                },
                {
                    key: 'developer-docs',
                    title: 'Developer Docs',
                    link: `/developer?club=${encodeURIComponent(clubSlug)}`,
                    icon: faBook,
                    external: true,
                    permissions: ['headless_api.manage']
                }
            ]
        },
        {
            title: 'Trainer Buchungen',
            link: `${routePrefix}/trainer-booking`,
            moduleKey: 'trainer-booking',
            icon: faChalkboardTeacher,
            tabs: [
                {
                    key: '',
                    title: 'Trainer',
                    icon: faSign
                },
                {
                    key: 'prices',
                    title: 'Preisregeln',
                    icon: faDollarSign,
                    permissions: ['trainer_booking.price_rule.view'],
                    group: 'settings'
                },
                {
                    key: 'blockings',
                    title: 'Sperrungen und Gruppen',
                    icon: faBan,
                    permissions: ['trainer_booking.blocking.view', 'trainer_booking.blocking_group.view']
                },
                {
                    key: 'bookings',
                    title: 'Bestätigte Buchungen',
                    icon: faUserCheck,
                    permissions: ['trainer_booking.booking.view']
                },
                {
                    key: 'requests',
                    title: 'Angefragte Buchungen',
                    icon: faUserClock,
                    permissions: ['trainer_booking.booking.view']
                },
                {
                    key: 'invoice-settings',
                    title: 'Rechnungseinstellungen',
                    icon: faFileInvoice,
                    permissions: ['user_invoice.view'],
                    group: 'settings'
                }
            ]
        },
        {
            title: i18n.t('bookings:nav.title'),
            link: `${routePrefix}/bookings`,
            moduleKey: 'bookings',
            icon: faTicketAlt,
            requiredPlan: 'eventtool',
            tabs: [
                {
                    key: '',
                    title: i18n.t('bookings:nav.bookings'),
                    icon: faUsers,
                    permissions: ['event_calendar.booking.view'],
                    badge: unreadBookingCount
                },
                {
                    key: 'archive',
                    title: i18n.t('bookings:nav.archive'),
                    icon: faArchive,
                    permissions: ['event_calendar.booking.view'],
                    group: 'settings'
                }
            ]
        },
        {
            title: 'Turnier Management',
            link: `${routePrefix}/tournaments`,
            moduleKey: 'tournaments',
            icon: faTrophy,
            experimental: 'turnaments',
            tabs: [
                {
                    key: '',
                    title: 'Turniere',
                    icon: faList,
                    permissions: ['tournament.view']
                }
            ],
            beta: true
        },
        {
            // Translated module (see docs/i18n.md) — titles resolve via i18n so the
            // sidebar follows the active locale. Caller must rebuild on language change.
            title: i18n.t('trainingsjournal:nav.title'),
            link: `${routePrefix}/journal`,
            moduleKey: 'journal',
            icon: faBook,
            requiredPlan: 'attendancelisttool',
            tabs: [
                {
                    key: '',
                    title: i18n.t('trainingsjournal:nav.entries'),
                    icon: faUsers,
                    permissions: ['trainingsjournal.entry.view']
                },
                {
                    key: 'templates',
                    title: i18n.t('trainingsjournal:nav.templates'),
                    icon: faBookmark,
                    permissions: ['trainingsjournal.template.view']
                },
                {
                    key: 'make-up',
                    title: i18n.t('trainingsjournal:nav.makeUp'),
                    icon: faRotateLeft,
                    permissions: ['trainingsjournal.entry.view']
                },
                {
                    key: 'trainings',
                    title: i18n.t('trainingsjournal:nav.trainings'),
                    icon: faChalkboardTeacher,
                    permissions: ['trainingsjournal.template.view'],
                    group: 'settings'
                },
                {
                    key: 'imports',
                    title: i18n.t('trainingsjournal:nav.imports'),
                    icon: faFileImport,
                    permissions: ['trainingsjournal.template.view'],
                    group: 'settings'
                },
                {
                    key: 'updates',
                    title: i18n.t('trainingsjournal:nav.updates'),
                    icon: faSyncAlt,
                    badge: updateCount,
                    permissions: ['trainingsjournal.template.view']
                },
                {
                    key: 'analysis',
                    title: i18n.t('trainingsjournal:nav.analysis'),
                    icon: faChartPie
                },
                {
                    key: 'invoices',
                    title: i18n.t('trainingsjournal:nav.invoices'),
                    icon: faFileInvoice,
                    group: 'settings',
                    tooltip: i18n.t('trainingsjournal:nav.invoicesTooltip')
                },
                {
                    key: 'grid',
                    title: i18n.t('trainingsjournal:nav.grid'),
                    icon: faTable,
                    permissions: ['trainingsjournal.entry.view', 'trainingsjournal.template.view']
                }
            ]
        },
        {
            // Successor of the classic training plan. Generally available since
            // 14.09.2026; the `trainingplantool` plan still decides who gets it.
            title: i18n.t('training-planner:nav.title'),
            link: `${routePrefix}/training-planner`,
            moduleKey: 'training-planner',
            icon: faChalkboardTeacher,
            requiredPlan: 'trainingplantool',
            beta: true,
            // Only the club-wide pages live here. Everything that works inside a
            // single plan — week grid, groups, participants, booking import —
            // moved into the plan's own sidebar, reachable by opening a plan.
            tabs: [
                {
                    key: '',
                    title: i18n.t('training-planner:nav.plans'),
                    icon: faLayerGroup,
                    permissions: ['training_planner.group.view'],
                    // Being inside a plan is still being in the plans section,
                    // so /plan/… must not leave the module unmarked.
                    activeKeys: ['plan']
                },
                {
                    key: 'migration',
                    title: i18n.t('training-planner:nav.migration'),
                    icon: faFileImport,
                    permissions: ['training_planner.migrate'],
                    group: 'settings'
                }
                // No club-wide settings page. Everything that was on it belongs
                // to a plan: the lanes moved first, then the tags — a tag now
                // names a condition in that plan's price list, which a
                // club-wide one could not. What was left was a page holding a
                // sentence about where its own contents had gone.
            ]
        },
        {
            // Aufgaben / Arbeitsdienste. Allgemein verfügbar seit 14.08.2026, das frühere
            // Flag `tasks_v1` ist entfernt (ClubModule::experimentalFeature()). Kein Plan
            // dahinter: das Modul schreibt nichts in fremde Domänen und kostet nichts extra.
            title: i18n.t('tasks:nav.title'),
            link: `${routePrefix}/tasks`,
            moduleKey: 'tasks',
            icon: faListCheck,
            beta: true,
            tabs: [
                {
                    key: '',
                    title: i18n.t('tasks:nav.tasks'),
                    icon: faList,
                    permissions: ['tasks.task.view']
                },
                {
                    key: 'archive',
                    title: i18n.t('tasks:nav.archive'),
                    icon: faBoxArchive,
                    permissions: ['tasks.task.view']
                }
            ]
        },
        {
            // Kostenlos, kein Plan: Terminals und Seiten sind reine Konfiguration.
            title: i18n.t('kiosk:nav.title'),
            link: `${routePrefix}/kiosk`,
            moduleKey: 'kiosk',
            icon: faTabletScreenButton,
            tabs: [
                {
                    key: '',
                    title: i18n.t('kiosk:nav.terminals'),
                    icon: faDesktop,
                    permissions: ['kiosk.terminal.view']
                },
                {
                    key: 'guide',
                    title: i18n.t('kiosk:nav.guide'),
                    icon: faBook,
                    permissions: ['kiosk.terminal.view']
                }
            ]
        },
        {
            title: i18n.t('invoice:nav.title'),
            link: `${routePrefix}/invoices`,
            moduleKey: 'invoices',
            icon: faFileInvoice,
            requiredPlan: 'invoicetool',
            tabs: invoiceTabs
        },
        {
            // Kasse & Shop. Allgemein verfügbar seit 14.08.2026, das frühere Flag `shop_v1`
            // ist entfernt (ClubModule::experimentalFeature()). Deliberately its own module
            // rather than another invoice tab: it grows into a full shop, and the invoice
            // module already carries a long tab list.
            title: i18n.t('shop:nav.title'),
            link: `${routePrefix}/shop`,
            moduleKey: 'shop',
            icon: faCashRegister,
            // Wie Banking am Rechnungs-Abo mitverkauft, und hier zwingend: jeder Ausgang der
            // Kasse schreibt eine UserInvoice — die Sammelabrechnung, die Sofortrechnung und
            // auch die Barzahlung —, und Ansehen/Stornieren/Mahnen dieser Belege hängt in
            // api.php am selben Plan. Ohne diesen Eintrag produziert ein Verein ohne
            // Rechnungs-Abo Belege in ein Modul, das er nicht öffnen kann. Muss zum
            // Middleware-Gate der Shop-Routen (routes/shop.php) passen — sonst zeigt die
            // Seite den Upgrade-Hinweis nicht, sondern läuft in 403er der einzelnen Requests.
            requiredPlan: 'invoicetool',
            beta: true,
            tabs: [
                {
                    key: '',
                    title: i18n.t('shop:nav.pos'),
                    icon: faCashRegister,
                    permissions: ['shop.entry.create']
                },
                {
                    key: 'accounts',
                    title: i18n.t('shop:nav.accounts'),
                    icon: faUsers,
                    permissions: ['shop.account.view']
                },
                {
                    key: 'billing',
                    title: i18n.t('shop:nav.billing'),
                    icon: faFileInvoice,
                    permissions: ['shop.billing.run']
                },
                {
                    // Same catalogue as the invoice module's product tab — the page renders
                    // the identical component, it is only reachable from both modules.
                    key: 'products',
                    title: i18n.t('shop:nav.products'),
                    icon: faBoxesStacked,
                    permissions: ['user_invoice.product.view']
                }
                // ! No settings tab here. The Einstellungen hub already lists this module
                // via buildShopSettings() in ui/SettingsPanel/settingsConfig.ts, which is
                // where every other module's configuration lives — a tab here as well put
                // the same link in the sidebar twice. The route /shop/settings is
                // unchanged and still reachable, only the duplicate entry is gone.
            ]
        },
        {
            title: i18n.t('access:nav.title'),
            link: `${routePrefix}/access`,
            moduleKey: 'access',
            icon: faKey,
            tabs: [
                {
                    key: '',
                    title: i18n.t('access:nav.virtualKeys'),
                    icon: faKey,
                    permissions: ['access_control.virtual_key.view']
                },
                {
                    key: 'openings',
                    title: i18n.t('access:nav.openings'),
                    icon: faHistory,
                    permissions: ['access_control.device.view']
                },
                {
                    key: 'devices',
                    title: i18n.t('access:nav.devices'),
                    icon: faLock,
                    permissions: ['access_control.device.view'],
                    group: 'settings'
                },
                {
                    key: 'gateways',
                    title: i18n.t('access:nav.gateways'),
                    icon: faServer,
                    permissions: ['access_control.gateway.view'],
                    group: 'settings'
                },
                {
                    key: 'integrations',
                    title: i18n.t('access:nav.integrations'),
                    icon: faPlug,
                    permissions: ['access_control.device.update'],
                    group: 'settings'
                }
            ]
        },
        {
            title: i18n.t('voucher:nav.title'),
            link: `${routePrefix}/vouchers`,
            moduleKey: 'vouchers',
            icon: faWindowMaximize,
            tabs: [
                {
                    key: '',
                    title: i18n.t('voucher:nav.vouchers'),
                    permissions: ['vouchers.voucher.view'],
                    icon: faWindowMaximize
                },
                {
                    key: 'templates',
                    title: i18n.t('voucher:nav.templates'),
                    permissions: ['vouchers.template.view'],
                    icon: faWindowRestore,
                    group: 'settings'
                }
            ]
        },
        {
            // Translated module (see docs/i18n.md) — titles resolve via i18n so the
            // sidebar follows the active locale. Caller must rebuild on language change.
            title: i18n.t('settings:nav.title'),
            link: `${routePrefix}/settings`,
            moduleKey: 'settings',
            virtual: true,
            icon: faCog,
            // Tabs derived from the shared settings config — single source of truth for
            // module name/icon/permissions. Adding a module there surfaces it here automatically.
            tabs: buildSettingsModules(i18n.getFixedT(null, 'settings'))
                .filter((m) => m.active)
                .map((m) => ({
                    key: m.segment,
                    title: m.name,
                    icon: m.icon,
                    link: `${routePrefix}/${m.segment}/settings`,
                    permissions: m.permissions,
                    experimental: m.experimental
                }))
        },
        {
            title: i18n.t('logs:nav.title'),
            link: `${routePrefix}/history`,
            moduleKey: 'history',
            icon: faHistory,
            infoBadge: {
                icon: faCircleInfo,
                tooltip: i18n.t('logs:nav.deletionHint')
            },
            tabs: [
                {
                    key: '',
                    title: i18n.t('logs:nav.overview'),
                    icon: faList,
                    permissions: ['club.settings']
                },
                {
                    key: 'club-member',
                    title: i18n.t('logs:nav.clubMember'),
                    icon: faUsers,
                    permissions: ['club.settings']
                },
                {
                    key: 'user-invoice',
                    title: i18n.t('logs:nav.userInvoice'),
                    icon: faFileInvoice,
                    permissions: ['club.settings']
                },
                {
                    key: 'space',
                    title: i18n.t('logs:nav.space'),
                    icon: faBorderAll,
                    permissions: ['club.settings']
                },
                {
                    key: 'event',
                    title: i18n.t('logs:nav.event'),
                    icon: faTicketAlt,
                    permissions: ['club.settings']
                },
                {
                    key: 'voucher',
                    title: i18n.t('voucher:nav.title'),
                    icon: faWindowMaximize,
                    permissions: ['club.settings']
                },
                {
                    key: 'trainingsjournal',
                    title: i18n.t('trainingsjournal:nav.title'),
                    icon: faBook,
                    permissions: ['club.settings']
                },
                {
                    key: 'training-planner',
                    // Titled from the logs namespace rather than the module's
                    // own, which exists only in German and English — a history
                    // tab is not the place to be the first untranslated string
                    // a Polish club sees.
                    title: i18n.t('logs:nav.trainingPlanner'),
                    icon: faChalkboardTeacher,
                    permissions: ['club.settings'],
                    // Hidden with the module itself. Every other tab here
                    // belongs to something every club has; this one would
                    // otherwise promise a history for a planner they cannot open.
                    experimental: 'training-planner',
                    beta: true
                },
                {
                    key: 'club-settings',
                    title: i18n.t('logs:nav.clubSettings'),
                    icon: faPuzzlePiece,
                    permissions: ['club.settings']
                }
            ]
        }
    ];
};
